import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  type NavigateOptions,
  type To,
} from "react-router-dom";
import { useCallback, type ReactNode } from "react";

import { useAuth } from "./auth-provider";
import { startViewTransition } from "./utils";
import { trackScreenView } from "./analytics";
import { BottomTabs } from "@/components/bottom-tabs";
import { OnboardingTour } from "@/components/onboarding-tour";

/**
 * Drop-in replacement for `useNavigate()` that wraps the underlying
 * `navigate(to, opts)` call in the View Transitions API. On browsers
 * without VT support (Safari <18, older Chromium) it degrades to a
 * plain navigate — see startViewTransition in lib/utils.ts.
 *
 * Existing `<Link>` clicks still go through React Router's own
 * navigate; the CSS pair in app.css cross-fades those anyway because
 * VT auto-fires on route pushes where a same-name element rehomes
 * (the trip card → trip hero cover). This hook is for programmatic
 * flows — successful mutations, redirects — where we want the fade
 * everywhere the platform supports it.
 */
export function useSoftNavigate() {
  const navigate = useNavigate();
  return useCallback(
    (to: To | number, options?: NavigateOptions) => {
      startViewTransition(() => {
        if (typeof to === "number") {
          navigate(to);
        } else {
          navigate(to, options);
        }
      });
    },
    [navigate],
  );
}

// Route modules
import BootRoute from "@/routes/index";
import AuthRoute from "@/routes/auth";
import DashboardRoute from "@/routes/dashboard";
import ProfileRoute from "@/routes/profile";
import TripCreateRoute from "@/routes/trip.create";
import TripLayoutRoute from "@/routes/trip.$id";
import TimelineTabRoute from "@/routes/trip.$id.timeline";
import PlanTabRoute from "@/routes/trip.$id.plan";
import ExpensesTabRoute from "@/routes/trip.$id.expenses";
import DocumentsTabRoute from "@/routes/trip.$id.documents";
import AddEventRoute from "@/routes/trip.$id.add-event";
import AddExpenseRoute from "@/routes/trip.$id.add-expense";
import RecordPaymentRoute from "@/routes/trip.$id.record-payment";
import TripSettingsRoute from "@/routes/trip.$id.settings";
import TripRecapRoute from "@/routes/trip.$id.recap";
import InviteRoute from "@/routes/invite.$token";
import NotFoundRoute from "@/routes/not-found";

/**
 * Guard: unauthenticated users are bounced to /auth with the original path preserved.
 */
function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center bg-bg">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/auth" state={{ from: location.pathname }} replace />;
  }
  return <>{children}</>;
}

export const router = createBrowserRouter([
  { path: "/", element: <BootRoute /> },
  { path: "/auth", element: <AuthRoute /> },

  // Invite-link accept page — public entry point (route decides itself
  // whether to bounce to /auth). Sits OUTSIDE RequireAuth so a fresh
  // visitor arriving from a shared URL sees the invite card before signing
  // in, not the auth guard.
  { path: "/invite/:token", element: <InviteRoute /> },

  {
    // BottomTabs is a sibling of <Outlet /> so it lives inside the
    // router context (needs useLocation) but next to whichever route
    // is currently mounted. It renders nothing on trip-detail routes;
    // the mount rule lives in components/bottom-tabs.tsx.
    // OnboardingTour renders nothing unless the tutorial is running.
    element: (
      <RequireAuth>
        <Outlet />
        <BottomTabs />
        <OnboardingTour />
      </RequireAuth>
    ),
    children: [
      { path: "/dashboard", element: <DashboardRoute /> },
      { path: "/profile", element: <ProfileRoute /> },
      { path: "/trip/create", element: <TripCreateRoute /> },
      {
        path: "/trip/:id",
        element: <TripLayoutRoute />,
        children: [
          { index: true, element: <Navigate to="timeline" replace /> },
          { path: "timeline", element: <TimelineTabRoute /> },
          { path: "plan", element: <PlanTabRoute /> },
          { path: "expenses", element: <ExpensesTabRoute /> },
          { path: "documents", element: <DocumentsTabRoute /> },
        ],
      },
      { path: "/trip/:id/add-event", element: <AddEventRoute /> },
      { path: "/trip/:id/add-expense", element: <AddExpenseRoute /> },
      { path: "/trip/:id/record-payment", element: <RecordPaymentRoute /> },
      { path: "/trip/:id/settings", element: <TripSettingsRoute /> },
      { path: "/trip/:id/recap", element: <TripRecapRoute /> },
    ],
  },

  { path: "*", element: <NotFoundRoute /> },
]);

// Screen-view tracking. A data router's location changes don't need a
// mounted React component to observe — router.subscribe() fires on every
// state transition, including loader/action states, so we only log when the
// pathname actually changed to avoid double-counting a single navigation.
let lastTrackedPath: string | null = null;
router.subscribe((state) => {
  const path = state.location.pathname;
  if (path === lastTrackedPath) return;
  lastTrackedPath = path;
  trackScreenView(path);
});
